import { activateLanguage, t, useStoreLanguage } from "../../../features/store-language/i18n.ts";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button, Form, Input } from "antd";
import {
  LockOutlined,
  MailOutlined,
  MobileOutlined,
} from "@ant-design/icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AppFeedback from "../../shared/AppFeedback.tsx";
import { getTenantInfo } from "../storefrontService.ts";
import CustomerAuthShell from "./CustomerAuthShell.tsx";
import { agencyContext, agencyLogin, agencyOtpLogin, sendAgencyLoginCode } from "./agencyIdentityService.ts";
import { showAgencyAccountLockout } from "./agencyAccountLockoutFeedback.ts";
import { normalizeLoginDigits } from "./customerIdentityService.ts";
import AgencyVerificationCodeInput from "./AgencyVerificationCodeInput.tsx";
import {
  clearAgencyOtpDraft, formatAgencyOtpCountdown, formatAgencyOtpTime, parseAgencyOtpTiming,
  readAgencyOtpDraft, readAgencyOtpWait, saveAgencyOtpDraft, type AgencyOtpTiming,
} from "./agencyOtpTiming.ts";
import { registrationOptions } from "./agencyRegistrationService.ts";
import "./CustomerLogin.css";

const MOBILE_PATTERN = /^09\d{9}$/;

type LoginValues = {
  mobile: string;
  password: string;
  code?: string;
};

function normalizeMobile(value: string): string {
  if (value.includes("@")) return value.trim().toLowerCase();
  return normalizeLoginDigits(value).trim().replace(/^(?:\+98|0098)/, "0");
}

function readCachedCompanyInfo(): any {
  try {
    const company = localStorage.getItem("company");
    return company ? JSON.parse(company) : null;
  } catch {
    return null;
  }
}

export default function AgencyLoginPage() {
  const storeLanguage = useStoreLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  useLayoutEffect(function () {
    const requestedLanguage = new URLSearchParams(location.search).get("language");
    if (requestedLanguage && ["fa", "en", "ar"].includes(requestedLanguage)) {
      activateLanguage(requestedLanguage);
    }
  }, [location.search]);
  const [form] = Form.useForm<LoginValues>();
  const [companyInfo, setCompanyInfo] = useState<any>(readCachedCompanyInfo);
  const [allowRegistration, setAllowRegistration] = useState(false);
  useEffect(function () {
    let current = true;
    registrationOptions().then(function (options) {
      if (current) setAllowRegistration(options.allowSelfRegistration);
    }).catch(function () { /* Keep sign-in available when registration options cannot be loaded. */ });
    return function () { current = false; };
  }, []);
  const [busy, setBusy] = useState(false);
  const locked = useRef(false);
  const [initialDraft] = useState(readAgencyOtpDraft);
  const [mode, setMode] = useState<"password" | "code">(function () {
    return initialDraft.timings[initialDraft.mobile] ? "code" : "password";
  });
  const previousLanguage = useRef(storeLanguage);
  const codeMode = mode === "code";
  const [sending, setSending] = useState(false);
  const [timings, setTimings] = useState(initialDraft.timings);
  const watchedMobile = Form.useWatch("mobile", form);
  const watchedCode = Form.useWatch("code", form);
  const mobile = normalizeMobile(watchedMobile ?? initialDraft.mobile);
  const emailRecipient = mobile.includes("@");
  const timing = timings[mobile];
  const sent = Boolean(timing);
  const codeRequested = timing?.codeRequested === true;
  const [now, setNow] = useState(Date.now);
  const serverNow = now + (timing?.serverOffset || 0);
  const secondsLeft = timing ? Math.max(0, Math.ceil((timing.retryAt - serverNow) / 1000)) : 0;
  const codeSecondsLeft = timing ? Math.max(0, Math.ceil((timing.expiresAt - serverNow) / 1000)) : 0;
  const canSubmitCode = codeRequested && /^\d{6}$/.test(watchedCode || "") && codeSecondsLeft > 0;

  useEffect(function () {
    if (previousLanguage.current === storeLanguage) return;
    previousLanguage.current = storeLanguage;
    setMode("password");
    form.resetFields(["mobile", "code", "password"]);
    form.setFieldValue("mobile", "");
  }, [storeLanguage, form]);

  useEffect(function () {
    function tick() { setNow(Date.now()); }
    const timer = window.setInterval(tick, 1000);
    window.addEventListener("focus", tick);
    document.addEventListener("visibilitychange", tick);
    return function () {
      window.clearInterval(timer);
      window.removeEventListener("focus", tick);
      document.removeEventListener("visibilitychange", tick);
    };
  }, []);

  function rememberTiming(phone: string, next: AgencyOtpTiming, successful = false) {
    setNow(Date.now());
    setTimings(function (current) {
      const updated = {
        ...current,
        [phone]: { ...next, codeRequested: successful || current[phone]?.codeRequested === true },
      };
      const recent = Object.fromEntries(Object.entries(updated).slice(-10));
      saveAgencyOtpDraft({ mobile: phone, timings: recent });
      return recent;
    });
  }

  function showWait(next: AgencyOtpTiming) {
    AppFeedback.error({
      title: t("هنوز زمان ارسال مجدد نرسیده است"),
      description: t("از ساعت {{time}} می‌توانید کد جدید درخواست کنید. اگر کد قبلی معتبر است، همان را وارد کنید.", {
        time: formatAgencyOtpTime(next.retryAt),
      }),
    });
  }

  function changeMode(next: "password" | "code") {
    if (locked.current) return;
    setMode(next);
    form.resetFields(["mobile", "code", "password"]);
    form.setFieldValue("mobile", "");
  }

  async function sendCode() {
    if (locked.current) return;
    if (timing && Date.now() + timing.serverOffset < timing.retryAt) {
      showWait(timing);
      return;
    }
    locked.current = true;
    setSending(true);
    let requestedMobile = mobile;
    try {
      const values = await form.validateFields(["mobile"]);
      requestedMobile = normalizeMobile(values.mobile);
      const result = await sendAgencyLoginCode(requestedMobile);
      const next = parseAgencyOtpTiming(result);
      if (!next) throw new Error(t("اطلاعات زمان اعتبار کد دریافت نشد؛ دوباره تلاش کنید."));
      rememberTiming(requestedMobile, next, true);
      form.resetFields(["code"]);
      AppFeedback.info({
        title: t("کد ورود ارسال می‌شود"),
        description: t("اعتبار کد چهار دقیقه است."),
      });
    } catch (error) {
      const existing = readAgencyOtpWait(error);
      if (existing) {
        // Recover the previous code window after reload or a request from another browser tab.
        rememberTiming(requestedMobile, existing);
        showWait(existing);
      } else if (error && typeof error === "object" && "errorFields" in error) {
        AppFeedback.error({ title: t("شماره موبایل یا ایمیل را وارد کنید.") });
      } else {
        AppFeedback.httpError(error);
      }
    } finally {
      locked.current = false;
      setSending(false);
    }
  }

  useEffect(function () {
    let active = true;
    getTenantInfo().then(function (info) {
      if (!active) return;
      setCompanyInfo(info);
      try {
        localStorage.setItem("company", JSON.stringify(info));
      } catch {
        // Keep the login usable when storage is unavailable.
      }
    }).catch(function () {
      // The cached identity, when present, is enough for the login presentation.
    });
    return function () {
      active = false;
    };
  }, [storeLanguage]);

  async function login(values: LoginValues) {
    if (locked.current) return;
    if (codeMode && !codeRequested) return;
    if (codeMode && timing && timing.expiresAt <= Date.now() + timing.serverOffset) {
      AppFeedback.error({ title: t("زمان کد تمام شد؛ کد جدید دریافت کنید.") });
      return;
    }
    locked.current = true;
    setBusy(true);
    try {
      const result = codeMode
        ? await agencyOtpLogin(normalizeMobile(values.mobile), normalizeLoginDigits(values.code || ""))
        : await agencyLogin(normalizeMobile(values.mobile), values.password);
      if (result.isAuthenticated) {
        clearAgencyOtpDraft();
        await agencyContext();
        localStorage.removeItem("jwt");
        localStorage.removeItem("user");
        navigate("/products", { replace: true });
      }
    } catch (error) {
      if (!showAgencyAccountLockout(error)) AppFeedback.httpError(error);
    } finally {
      locked.current = false;
      setBusy(false);
    }
  }

  return (
    <CustomerAuthShell
      companyInfo={companyInfo}
      title={t("ورود به پرتال")}
      description=""
      showLanguageSelector
      footer={allowRegistration ? (
        <div className="customer-auth-register-prompt">
          <span>{t("حساب کاربری ندارید؟")}</span>
          <Link
            className="customer-auth-register-link"
            to="/register"
          >
            {t("ثبت‌نام نمایندگی")}
          </Link>
        </div>
      ) : undefined}
    >
      <div
        className="customer-auth-mode"
        aria-label={t("روش ورود")}
      >
        <button
          type="button"
          className={!codeMode ? "is-active" : undefined}
          aria-pressed={!codeMode}
          disabled={busy || sending}
          onClick={function () { changeMode("password"); }}
        >
          <LockOutlined />
          {t("رمز عبور")}
        </button>
        <button
          type="button"
          className={codeMode ? "is-active" : undefined}
          aria-pressed={codeMode}
          disabled={busy || sending}
          onClick={function () { changeMode("code"); }}
        >
          {t("کد تأیید")}
        </button>
      </div>

      <Form
        form={form}
        initialValues={{ mobile: initialDraft.mobile }}
        layout="vertical"
        requiredMark={false}
        onFinish={login}
        onFinishFailed={function () {
          AppFeedback.error({ title: t("اطلاعات ورود را کامل کنید.") });
        }}
        className="customer-auth-form"
      >
        <Form.Item
          name="mobile"
          label={t("شماره موبایل یا ایمیل")}
          rules={[
            { required: true, message: t("شماره موبایل یا ایمیل را وارد کنید.") },
            {
              validator: function (_, value?: string) {
                const isEmail = Boolean(value?.includes("@"));
                if (!value || (isEmail ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()) : MOBILE_PATTERN.test(normalizeMobile(value)))) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(t("شماره موبایل یا ایمیل را وارد کنید.")),
                );
              },
            },
          ]}
        >
          <Input
            prefix={emailRecipient ? <MailOutlined /> : <MobileOutlined />}
            placeholder={t("شماره موبایل یا ایمیل")}
            inputMode="text"
            autoComplete="username"
            disabled={busy || sending}
            dir="ltr"
            onChange={function (event) {
              if (normalizeMobile(event.target.value) === mobile) return;
              form.resetFields(["code"]);
            }}
          />
        </Form.Item>

        {!codeMode ? (
          <>
            <Form.Item
              name="password"
              label={t("رمز عبور")}
              rules={[{ required: true, message: t("رمز عبور را وارد کنید.") }]}
            >
              <Input.Password
                placeholder={t("رمز عبور خود را وارد کنید")}
                disabled={busy || sending}
                autoComplete="current-password"
              />
            </Form.Item>

          </>
        ) : (
          <>
            {codeRequested && (
              <Form.Item
                name="code"
                label={emailRecipient ? t("کد تأیید ایمیل") : t("کد تأیید شمارهٔ همراه")}
                validateTrigger="onBlur"
                rules={[
                  { required: true, message: emailRecipient ? t("کد ایمیلی را وارد کنید.") : t("کد پیامکی را وارد کنید.") },
                  { pattern: /^\d{6}$/, message: t("کد تأیید باید شش رقم باشد.") },
                ]}
              >
                <AgencyVerificationCodeInput
                  focusRequest={timing?.expiresAt}
                  disabled={busy || sending || codeSecondsLeft === 0}
                  onComplete={function (code) {
                    if (!(emailRecipient ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mobile) : MOBILE_PATTERN.test(mobile))) return;
                    void login({ ...form.getFieldsValue(true), code });
                  }}
                />
              </Form.Item>
            )}
            {sent && (
              <div className="customer-login-otp-status">
                <div
                  role="timer"
                  aria-live="off"
                >
                  {t("اعتبار کد")}: <bdi>{formatAgencyOtpCountdown(codeSecondsLeft)}</bdi>
                </div>
                {codeSecondsLeft === 0 && (
                  <p role="status">{t("زمان کد تمام شد؛ کد جدید دریافت کنید.")}</p>
                )}
              </div>
            )}
            <Form.Item className="customer-invitation-code-action">
              <Button
                block
                onClick={sendCode}
                loading={sending}
                aria-busy={sending}
                disabled={busy || sending || secondsLeft > 0}
              >
                {secondsLeft > 0
                  ? t("ارسال مجدد پس از {{seconds}} ثانیه", { seconds: secondsLeft })
                  : sent ? t("ارسال مجدد کد تأیید") : t("دریافت کد تأیید")}
              </Button>
            </Form.Item>
          </>
        )}

        <div className="customer-auth-actions-group">
          <Form.Item className="customer-auth-submit">
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={busy}
              disabled={busy || sending || (codeMode && !canSubmitCode)}
            >
              {t("ورود به پورتال نمایندگان")}
            </Button>
          </Form.Item>
        </div>
      </Form>
    </CustomerAuthShell>
  );
}
